import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Formik } from 'formik'
import * as yup from 'yup'
import {
  FormHeader,
  FormTitle,
  InputsContainer,
  ButtonsContainer,
  LinkButton,
  FormContainer,
  Wrapper,
  FormContent,
  FormSubtitle
} from '../../../../components/ui/form'
import Input from '../../../../components/ui/input'
import MultiSelect from '../../../../components/ui/multiSelect'
import { Button } from '../../../../components/ui/button'
import { createUser, getDetailedGroupRoles, getUserById, updateUser } from '../../../../api'
import { hideLoader, showLoader } from '../../../../actions'
import to from 'await-catch'
import { connect } from 'react-redux'
import { mapToOptions } from '../../../../utils/mapToOptions'
import Feedback from '../../../../components/feedback'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { AUTOMANAGEMENTFUNDER_GROUP, FUNDER_GROUP, PATIENT_GROUP } from '../../../../constants'
import { PREGNANCYPLANNER_GROUP, PATIENTADMIN } from '../../../../constants'
import Select from '../../../../components/ui/select'
import { useLocation } from 'react-router-dom'

const CreateUser = ({ auth, showLoader, hideLoader }) => {
  const [isSent, setIsSent] = useState({ isSent: false, isSentWithError: false })
  const [roleGroups, setRoleGroups] = useState([])
  const [roles, setRoles] = useState([])
  const [metadatas, setMetadatas] = useState([])
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [formData, setFormData] = useState(null)
  const history = useHistory()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const editUserId = location?.state?.idUsuario || searchParams.get('idUsuario')
  const [isEditMode, setIsEditMode] = useState(false)
  const [userData, setUserData] = useState(null)
  const [initialValues, setInitialValues] = useState({
    roles: [],
    name: '',
    lastname: '',
    email: ''
  })
  const [canEditRoles, setCanEditRoles] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      // Detectar si estamos en modo edición
      const isEdit = location.pathname.includes('/modificar')
      setIsEditMode(isEdit)

      if (isEdit && !editUserId) {
        history.push('/inicio/usuarios')
        return
      }

      // Gestores de usuarios no deberían poder modificar roles
      if (auth?.gruposRoles?.includes(PREGNANCYPLANNER_GROUP) || auth?.roles?.includes(PATIENTADMIN)) {
        setCanEditRoles(false)
      }

      const [err, { data: rolesData } = {}] = await to(getDetailedGroupRoles())
      if (!err && rolesData) {
        let filteredRoleGroups = rolesData.filter(rg => rg.clave !== PATIENT_GROUP)

        if (auth?.gruposRoles?.includes(AUTOMANAGEMENTFUNDER_GROUP)) {
          filteredRoleGroups = filteredRoleGroups.filter(rg => rg.clave === FUNDER_GROUP)
        }

        const allRoles = filteredRoleGroups
          .map(rg => rg.roles)
          .reduce((a, b) => a.concat(b), [])

        setRoleGroups(filteredRoleGroups)
        setRoles(allRoles)

        // Si estamos en modo edición, cargar datos del usuario
        if (isEdit && editUserId) {
          const [userErr, { data: user } = {}] = await to(getUserById(editUserId))
          if (!userErr && user) {
            setUserData(user)

            const rolesWithUserAssigned = [
              ...allRoles,
              ...user.roles.filter(userRole => !allRoles.some(role => role.clave === userRole.clave))
            ]
            setRoles(rolesWithUserAssigned)

            const userRoles = user.roles.map(rg => rg.clave).reduce((a, b) => a.concat(b), [])

            const metaOptions = filteredRoleGroups
              .map(rg => rg.metadatos)
              .reduce((a, b) => a.concat(b), [])

            const userMetadata = user.metadatoValores.map(mv => {
              const metadata = { ...mv.metadato }
              metadata.valor = mv.valor
              metadata.opciones = metadata.tipo === 'Dropdown'
                ? metaOptions.find(mo => mo.idMetadato === metadata.idMetadato)?.opciones || []
                : []
              return metadata
            })

            user.gruposRoles.forEach(group => {
              const foundGroup = filteredRoleGroups.find(gr => gr.idGrupoRol === group.idGrupoRol)
              if (foundGroup) {
                foundGroup.metadatos.forEach(metadata => {
                  const foundMetadata = userMetadata.find(m => m.idMetadato === metadata.idMetadato)
                  if (!foundMetadata) {
                    const metadataToAdd = { ...metadata }
                    metadataToAdd.valor = ''
                    userMetadata.push(metadataToAdd)
                  }
                })
              }
            })

            setMetadatas(userMetadata)

            const metadataInitialValues = userMetadata.reduce((obj, { clave, valor }) => {
              return { ...obj, [clave]: valor }
            }, {})

            setInitialValues({
              roles: userRoles,
              name: user.nombre,
              lastname: user.apellido,
              email: user.email,
              ...metadataInitialValues
            })
          }
        } else {
          setUserData(null)
          setMetadatas([])
          setInitialValues({
            roles: [],
            name: '',
            lastname: '',
            email: ''
          })
        }
      }
    }

    loadData()
  }, [auth, editUserId, history, location.pathname])

  const updateMetadatas = (selectedRoleClaves, formikSetFieldValue) => {
    const selectedRoleGroups = roleGroups.filter(rg =>
      roles
        .filter(role => selectedRoleClaves.some(rc => rc === role.clave))
        .map(role => role.grupoRol.clave)
        .some(rgc => rgc === rg.clave)
    )

    const allMetadatas = selectedRoleGroups
      .map(rg => rg.metadatos)
      .reduce((a, b) => a.concat(b), [])
      .filter(
        (metadata, index, self) =>
          index === self.findIndex(m => m.idMetadato === metadata.idMetadato) && metadata.visible
      )

    allMetadatas.forEach(metadata => {
      formikSetFieldValue(metadata.clave, '')
    })

    setMetadatas(allMetadatas)
  }

  const renderPermits = selectedRoleClaves => {
    const selectedRoles = roles.filter(role =>
      selectedRoleClaves.some(rc => rc === role.clave)
    )
    const permits = selectedRoles
      .map(role => role.permisos)
      .reduce((a, b) => a.concat(b), [])
      .filter(
        (permit, index, self) => index === self.findIndex(p => p.idPermiso === permit.idPermiso)
      )

    return permits.map(permit => permit.nombre).join(', ')
  }

  const getRoleOptions = () => {
    return roles.map(role => ({
      value: role.clave,
      label: role.nombre
    }))
  }

  const handleFormSubmit = async (formValues, formikHelpers) => {
    const selectedRoles = roles.filter(role =>
      formValues.roles.some(r => r === role.clave)
    )

    const metadatoValores = metadatas.map(metadata => ({
      metadato: metadata,
      valor: formValues[metadata.clave] || '',
      ...(isEditMode && userData ? { idUsuario: userData.idUsuario } : {})
    }))

    const parsedValues = {
      ...(isEditMode && userData ? userData : {}),
      nombre: formValues.name,
      apellido: formValues.lastname,
      email: formValues.email,
      roles: selectedRoles,
      metadatoValores: metadatoValores,
      roleClaves: formValues.roles
    }

    setFormData(parsedValues)
    setShowConfirmation(true)
  }

  const handleFinalSubmit = async () => {
    try {
      showLoader()

      const { roleClaves, ...requestBody } = formData

      const [err] = isEditMode
        ? await to(updateUser(requestBody))
        : await to(createUser(requestBody))

      if (err) throw new Error('Error en el fetch')

      setIsSent({ isSent: true, isSentWithError: false })
      setShowConfirmation(false)
    } catch (error) {
      console.error('Error:', error)
      setIsSent({ isSent: true, isSentWithError: true })
      setShowConfirmation(false)
    } finally {
      hideLoader()
    }
  }

  const handleBackToForm = () => {
    setShowConfirmation(false)
  }

  const onClickButton = () => {
    history.push('/inicio/usuarios')
  }

  const getRoleNames = roleClaves => {
    return roles
      .filter(role => roleClaves?.includes(role.clave))
      .map(role => role.nombre)
      .join(', ')
  }

  const getMetadataValue = clave => {
    const metadataValue = formData?.metadatoValores?.find(mv => mv.metadato.clave === clave)
    if (!metadataValue) return null

    const metadata = metadatas.find(m => m.clave === clave)
    if (metadata?.tipo === 'Dropdown') {
      const option = metadata.opciones.find(opt => opt.id === metadataValue.valor)
      return option?.valueText || metadataValue.valor
    }
    return metadataValue.valor
  }

  // Pantalla de confirmación
  if (showConfirmation && formData) {
    return (
      <Wrapper>
        <FormContainer justifyMobile="center" align="start" margin="0 0">
          <FormHeader align="start">
            <FormTitle align="start">{isEditMode ? 'Confirmación de edición' : 'Confirmación de alta'}</FormTitle>
          </FormHeader>
          <ConfirmationContent>
            <ConfirmationSection>
              <ConfirmationLabel>Rol</ConfirmationLabel>
              <ConfirmationValue>{getRoleNames(formData.roleClaves)}</ConfirmationValue>
            </ConfirmationSection>

            {formData.roleClaves?.length > 0 && (
              <ConfirmationSection>
                <ConfirmationPermitsBox>
                  <PermitTitle>El usuario podrá ver: </PermitTitle>
                  <Permit>{renderPermits(formData.roleClaves)}</Permit>
                </ConfirmationPermitsBox>
              </ConfirmationSection>
            )}

            <ConfirmationSection>
              <ConfirmationLabel>Nombre y apellido</ConfirmationLabel>
              <ConfirmationValue>
                {formData.nombre} {formData.apellido}
              </ConfirmationValue>
            </ConfirmationSection>

            <ConfirmationSection>
              <ConfirmationLabel>Email</ConfirmationLabel>
              <ConfirmationValue>{formData.email}</ConfirmationValue>
            </ConfirmationSection>

            {metadatas.map(
              (metadata, index) =>
                getMetadataValue(metadata.clave) && (
                  <ConfirmationSection key={index}>
                    <ConfirmationLabel>{metadata.nombre}</ConfirmationLabel>
                    <ConfirmationValue>{getMetadataValue(metadata.clave)}</ConfirmationValue>
                  </ConfirmationSection>
                )
            )}
          </ConfirmationContent>
          <ButtonsContainer>
            <Button
              label={isEditMode ? 'Finalizar edición' : 'Finalizar alta'}
              $variant="primary"
              size="mobile"
              onClick={handleFinalSubmit}
            />
            <LinkButton onClick={handleBackToForm}>Cancelar</LinkButton>
          </ButtonsContainer>
        </FormContainer>
      </Wrapper>
    )
  }

  return !isSent.isSent ? (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={yup.object().shape({
        roles: yup.array().min(1, 'Debe seleccionar al menos un rol').required('Debe seleccionar al menos un rol'),
        name: yup.string().required('Campo requerido'),
        lastname: yup.string().required('Campo requerido'),
        email: yup
          .string()
          .email('Email no válido')
          .required('Campo requerido'),
        ...metadatas.reduce((acc, metadata) => {
          acc[metadata.clave] = yup.string().required('Campo requerido')
          return acc
        }, {})
      })}
      onSubmit={handleFormSubmit}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => {
        const handleRolesChange = eventOrRoles => {
          const roleValues = Array.isArray(eventOrRoles)
            ? eventOrRoles
            : eventOrRoles?.target?.value

          const rolesArray = Array.isArray(roleValues) ? roleValues : []
          const roleClaves = rolesArray.map(role => role?.value || role)
          setFieldValue('roles', roleClaves)
          updateMetadatas(roleClaves, setFieldValue)
        }

        return (
          <Wrapper>
            <FormContainer justifyMobile="center" align="start" margin="0 0">
              <FormHeader align="start">
                <FormTitle>{isEditMode ? 'Modificar usuario' : 'Nuevo usuario'}</FormTitle>
              </FormHeader>
              <FormContent as="form">
                <InputsContainer>
                  <MultiSelect
                    label="Rol"
                    name="roles"
                    value={values.roles}
                    onChange={handleRolesChange}
                    options={getRoleOptions()}
                    error={touched.roles && errors.roles}
                    multi
                    isClearable
                    padding="10px 16px"
                    placeholder='Seleccioná uno o más roles'
                    disabled={!canEditRoles}
                  />
                  {touched.roles && errors.roles && (
                    <ErrorText>{errors.roles}</ErrorText>
                  )}
                  {values.roles.length > 0 && (
                    <PermitsContainer>
                      <PermitTitle>El usuario podrá ver: </PermitTitle>
                      <Permit>{renderPermits(values.roles)}</Permit>
                    </PermitsContainer>
                  )}
                  <Input
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    label="Nombre"
                    fullWidth
                    placeholder={"Ingresá tu nombre"}
                  />
                  {touched.name && errors.name && (
                    <ErrorText>{errors.name}</ErrorText>
                  )}
                  <Input
                    name="lastname"
                    value={values.lastname}
                    onChange={handleChange}
                    label="Apellido"
                    fullWidth
                    placeholder={"Ingresá tu apellido"}
                  />
                  {touched.lastname && errors.lastname && (
                    <ErrorText>{errors.lastname}</ErrorText>
                  )}
                  <Input
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    label="Email"
                    fullWidth
                    placeholder={"Ingresá tu email"}
                  />
                  {touched.email && errors.email && (
                    <ErrorText>{errors.email}</ErrorText>
                  )}

                  {metadatas.length > 0 &&
                    metadatas.map((metadata, index) => {
                      if (metadata.tipo === 'Dropdown') {
                        return (
                            <Select
                              label={metadata.nombre}
                              name={metadata.clave}
                              value={values[metadata.clave] || ''}
                              onChange={handleChange}
                              options={mapToOptions(metadata.opciones, 'id', 'valueText')}
                              error={touched[metadata.clave] && errors[metadata.clave]}
                            />
                        )
                      }
                      return (
                        <>
                          <Input
                            name={metadata.clave}
                            value={values[metadata.clave] || ''}
                            onChange={handleChange}
                            label={metadata.nombre}
                            fullWidth
                          />
                          {touched[metadata.clave] && errors[metadata.clave] && (
                            <ErrorText>{errors[metadata.clave]}</ErrorText>
                          )}
                        </>
                      )
                    })}
                </InputsContainer>
                <ButtonsContainer>
                  <Button
                    label={isEditMode ? 'Editar usuario' : 'Crear usuario'}
                    $variant="primary"
                    size="mobile"
                    type="submit"
                    onClick={handleSubmit}
                  />
                </ButtonsContainer>
              </FormContent>
            </FormContainer>
          </Wrapper>
        )
      }}
    </Formik>
  ) : (
    <Feedback
      title={!isSent.isSentWithError
        ? (isEditMode ? "El usuario fue modificado con éxito" : "El usuario fue creado con éxito")
        : (isEditMode ? "Hubo un error al modificar el usuario" : "Hubo un error al crear el usuario")}
      secondaryButtonLabel={isEditMode ? null : "Crear otro usuario"}
      onClickSecondaryButton={isEditMode ? null : () => setIsSent({ isSent: false, isSentWithError: false })}
      onClickButton={onClickButton}
      buttonLabel='Volver'
    />
  )
}

const mapDispatchToProps = {
  showLoader,
  hideLoader
}

const mapStateToProps = ({ auth }) => ({ auth })

export default connect(mapStateToProps, mapDispatchToProps)(CreateUser)

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    color: ${props => props.theme.colors.text.primaryText};
  }
`

const SelectField = styled.select`
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.grey};
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.green};
  }
`

const ErrorText = styled.span`
  color: ${props => props.theme.colors.feedback.error};
  font-size: 12px;
  margin-top: 4px;
`

const PermitsContainer = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  background-color: #EFF5FF;
`

const PermitTitle = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.brand.blueOmint};
`

const Permit = styled.span`
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
  color: ${props => props.theme.colors.brand.blueOmint};
`

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.brand.blueOmint};
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  text-decoration: none;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`

const ConfirmationContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 24px 0;
`

const ConfirmationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ConfirmationLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.primaryText};
`

const ConfirmationValue = styled.span`
  font-size: 16px;
  color: ${props => props.theme.colors.black};
`

const ConfirmationPermitsBox = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  background-color: #EFF5FF;
  border-radius: 4px;
`